import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { eventsApi } from '@/api/events'
import { usersApi } from '@/api/users'
import type { Event, EventStats, TicketTypeStat, EventVendor, CashRegisterSettlement } from '@/types/event'
import type { User } from '@/types/auth'
import { Badge } from '@/components/ui/Badge'
import { TicketTypeBadge } from '@/components/events/TicketTypeBadge'
import { Spinner } from '@/components/ui/Spinner'
import { toast } from '@/store/toastStore'
import { useAuthStore } from '@/store/authStore'
import { formatFCFA, formatDate } from '@/lib/utils'

const CATEGORY_COLORS = [
  { bg: 'bg-slate-500', light: 'bg-slate-50', border: 'border-slate-400', text: 'text-slate-700', bar: 'bg-slate-500' },
  { bg: 'bg-primary-600', light: 'bg-primary-50', border: 'border-primary-400', text: 'text-primary-700', bar: 'bg-primary-500' },
  { bg: 'bg-amber-500', light: 'bg-amber-50', border: 'border-amber-400', text: 'text-amber-700', bar: 'bg-amber-500' },
  { bg: 'bg-rose-500', light: 'bg-rose-50', border: 'border-rose-400', text: 'text-rose-700', bar: 'bg-rose-500' },
  { bg: 'bg-teal-500', light: 'bg-teal-50', border: 'border-teal-400', text: 'text-teal-700', bar: 'bg-teal-500' },
]

function KpiCard({ label, value, sub, icon, accent }: {
  label: string
  value: string | number
  sub?: string
  icon: React.ReactNode
  accent: string
}) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm flex items-start gap-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${accent}`}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1">{label}</p>
        <p className="text-2xl font-bold text-gray-900 font-mono leading-none">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
      </div>
    </div>
  )
}

function SellThroughBar({ sold, capacity, colorClass }: { sold: number; capacity: number; colorClass: string }) {
  if (capacity === 0) return <span className="text-xs text-gray-400 italic">Illimité</span>
  const pct = Math.min(100, Math.round((sold / capacity) * 100))
  return (
    <div className="flex items-center gap-2 min-w-[100px]">
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${colorClass}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-mono text-gray-500 w-8 text-right">{pct}%</span>
    </div>
  )
}

function StatCard({ stat, index }: { stat: TicketTypeStat; index: number }) {
  const color = CATEGORY_COLORS[index % CATEGORY_COLORS.length]
  const pct = stat.capacity > 0 ? Math.min(100, Math.round((stat.sold / stat.capacity) * 100)) : null

  return (
    <div className={`bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden`}>
      <div className={`h-1.5 w-full ${color.bg}`} />
      <div className="p-5">
        <div className="flex items-center justify-between mb-4">
          <TicketTypeBadge name={stat.name} sortOrder={index} size="md" />
          {pct !== null && (
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${color.light} ${color.text}`}>{pct}% vendu</span>
          )}
        </div>
        <p className="text-2xl font-bold text-gray-900 font-mono">{formatFCFA(stat.revenue)}</p>
        <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
          <span><span className="font-semibold text-gray-700 font-mono">{stat.sold}</span> vendus</span>
          {stat.capacity > 0 && (
            <span><span className="font-semibold text-gray-700 font-mono">{stat.capacity}</span> capacité</span>
          )}
          {stat.entries > 0 && (
            <span><span className="font-semibold text-gray-700 font-mono">{stat.entries}</span> entrées</span>
          )}
        </div>
        {pct !== null && (
          <div className="mt-3 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full ${color.bar}`} style={{ width: `${pct}%` }} />
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Channel breakdown ────────────────────────────────────────────────────────

function ChannelBreakdownCard({ stats }: { stats: EventStats }) {
  const total = stats.tickets_sold_total || 1
  const onlinePct = Math.round((stats.online_sold / total) * 100)
  const physicalPct = Math.round((stats.physical_sold / total) * 100)

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100">
        <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <svg className="w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
          </svg>
          Canaux de vente
        </h2>
      </div>
      <div className="grid grid-cols-2 divide-x divide-gray-100">
        <div className="p-5">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">En ligne</p>
          <p className="text-2xl font-bold text-gray-900 font-mono">{stats.online_sold}</p>
          <p className="text-xs text-gray-400 mt-0.5">{formatFCFA(stats.online_revenue)}</p>
          <div className="mt-3 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-primary-500 rounded-full" style={{ width: `${onlinePct}%` }} />
          </div>
          <p className="text-xs text-gray-400 mt-1">{onlinePct}% du total</p>
        </div>
        <div className="p-5">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Physique</p>
          <p className="text-2xl font-bold text-gray-900 font-mono">{stats.physical_sold}</p>
          <p className="text-xs text-gray-400 mt-0.5">{formatFCFA(stats.physical_revenue)}</p>
          <div className="mt-3 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-orange-500 rounded-full" style={{ width: `${physicalPct}%` }} />
          </div>
          <p className="text-xs text-gray-400 mt-1">{physicalPct}% du total</p>
        </div>
      </div>
    </div>
  )
}

// ─── Vendor assignment panel ───────────────────────────────────────────────────

interface VendorPanelProps {
  eventId: number
  vendors: EventVendor[]
  onVendorsChange: (vendors: EventVendor[]) => void
}

function VendorPanel({ eventId, vendors, onVendorsChange }: VendorPanelProps) {
  const [allVendors, setAllVendors] = useState<User[]>([])
  const [showAssign, setShowAssign] = useState(false)
  const [selectedId, setSelectedId] = useState<number | ''>('')
  const [assigning, setAssigning] = useState(false)
  const [removingId, setRemovingId] = useState<number | null>(null)

  useEffect(() => {
    if (showAssign) {
      usersApi.listVendors().then(setAllVendors).catch(() => {})
    }
  }, [showAssign])

  const unassigned = allVendors.filter((v) => !vendors.find((ev) => ev.id === v.id))

  const handleAssign = async () => {
    if (!selectedId) return
    setAssigning(true)
    try {
      await eventsApi.assignVendor(eventId, Number(selectedId))
      const updated = await eventsApi.listVendors(eventId)
      onVendorsChange(updated)
      setShowAssign(false)
      setSelectedId('')
      toast.success('Vendeur affecté.')
    } catch {
      toast.error('Impossible d\'affecter ce vendeur.')
    } finally {
      setAssigning(false)
    }
  }

  const handleRemove = async (vendorId: number) => {
    setRemovingId(vendorId)
    try {
      await eventsApi.removeVendor(eventId, vendorId)
      onVendorsChange(vendors.filter((v) => v.id !== vendorId))
      toast.success('Vendeur retiré.')
    } catch {
      toast.error('Impossible de retirer ce vendeur.')
    } finally {
      setRemovingId(null)
    }
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <svg className="w-4 h-4 text-orange-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/>
            <path d="M16 10a4 4 0 01-8 0"/>
          </svg>
          Vendeurs affectés
        </h2>
        <button
          onClick={() => setShowAssign((v) => !v)}
          className="flex items-center gap-1.5 text-xs font-semibold text-primary-600 hover:text-primary-700 px-2.5 py-1.5 rounded-lg hover:bg-primary-50 transition-colors"
        >
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Affecter
        </button>
      </div>

      {showAssign && (
        <div className="px-5 py-3.5 border-b border-gray-100 bg-gray-50/60 flex items-center gap-2">
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value === '' ? '' : Number(e.target.value))}
            className="flex-1 text-sm border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white"
          >
            <option value="">Sélectionner un vendeur…</option>
            {unassigned.map((v) => (
              <option key={v.id} value={v.id}>{v.name} — {v.email}</option>
            ))}
          </select>
          <button
            onClick={handleAssign}
            disabled={!selectedId || assigning}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-xl transition-colors disabled:opacity-50"
          >
            {assigning ? <Spinner className="w-3.5 h-3.5 text-white" /> : 'Affecter'}
          </button>
          <button onClick={() => setShowAssign(false)} className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      )}

      {vendors.length === 0 ? (
        <div className="px-5 py-8 text-center">
          <p className="text-sm text-gray-400">Aucun vendeur affecté</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-50">
          {vendors.map((v) => (
            <div key={v.id} className="px-5 py-3.5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-xl bg-orange-100 text-orange-700 flex items-center justify-center text-xs font-bold shrink-0">
                  {v.name[0].toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{v.name}</p>
                  <p className="text-xs text-gray-400 font-mono">{v.tickets_sold} billets · {formatFCFA(v.total_amount)}</p>
                </div>
              </div>
              <button
                onClick={() => handleRemove(v.id)}
                disabled={removingId === v.id}
                className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40"
              >
                {removingId === v.id
                  ? <Spinner className="w-3.5 h-3.5" />
                  : <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                }
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Settlement section ────────────────────────────────────────────────────────

interface SettlementSectionProps {
  eventId: number
  vendors: EventVendor[]
  settlements: CashRegisterSettlement[]
  onSettlementCreated: (s: CashRegisterSettlement) => void
}

function SettlementSection({ eventId, vendors, settlements, onSettlementCreated }: SettlementSectionProps) {
  const overlayRef = useRef<HTMLDivElement>(null)
  const [showModal, setShowModal] = useState(false)
  const [selectedVendorId, setSelectedVendorId] = useState<number | ''>('')
  const [expectedAmount, setExpectedAmount] = useState<number | null>(null)
  const [actualAmount, setActualAmount] = useState('')
  const [notes, setNotes] = useState('')
  const [loadingBalance, setLoadingBalance] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const handleVendorChange = async (id: number | '') => {
    setSelectedVendorId(id)
    setExpectedAmount(null)
    if (!id) return
    setLoadingBalance(true)
    try {
      const balance = await eventsApi.vendorBalance(eventId, id)
      setExpectedAmount(balance.total_amount ?? 0)
    } catch {
      setExpectedAmount(0)
    } finally {
      setLoadingBalance(false)
    }
  }

  const handleSubmit = async () => {
    if (!selectedVendorId || actualAmount === '') return
    setSubmitting(true)
    try {
      const settlement = await eventsApi.settle(eventId, Number(selectedVendorId), {
        actual_amount: parseInt(actualAmount, 10),
        notes: notes.trim() || undefined,
      })
      onSettlementCreated(settlement)
      setShowModal(false)
      setSelectedVendorId('')
      setActualAmount('')
      setNotes('')
      setExpectedAmount(null)
      toast.success('Arrêté de caisse effectué.')
    } catch {
      toast.error('Impossible d\'effectuer l\'arrêté.')
    } finally {
      setSubmitting(false)
    }
  }

  const discrepancy = actualAmount !== '' && expectedAmount !== null
    ? parseInt(actualAmount, 10) - expectedAmount
    : null

  return (
    <>
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <svg className="w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/>
            </svg>
            Arrêtés de caisse
          </h2>
          {vendors.length > 0 && (
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-1.5 text-xs font-semibold text-primary-600 hover:text-primary-700 px-2.5 py-1.5 rounded-lg hover:bg-primary-50 transition-colors"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Effectuer un arrêté
            </button>
          )}
        </div>

        {settlements.length === 0 ? (
          <div className="px-5 py-8 text-center">
            <p className="text-sm text-gray-400">Aucun arrêté de caisse enregistré</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50/70">
                  {['Vendeur', 'Attendu', 'Remis', 'Écart', 'Par', 'Date'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {settlements.map((s) => (
                  <tr key={s.id} className="border-t border-gray-50 hover:bg-gray-50/60 transition-colors">
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900">{s.vendor.name}</td>
                    <td className="px-4 py-3 text-sm font-mono text-gray-700">{formatFCFA(s.expected_amount)}</td>
                    <td className="px-4 py-3 text-sm font-mono text-gray-700">{formatFCFA(s.actual_amount)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-sm font-mono font-semibold ${s.discrepancy === 0 ? 'text-gray-400' : s.discrepancy > 0 ? 'text-success-600' : 'text-red-600'}`}>
                        {s.discrepancy > 0 ? '+' : ''}{formatFCFA(s.discrepancy)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{s.settled_by.name}</td>
                    <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                      {new Date(s.settled_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div
          ref={overlayRef}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm"
          onClick={(e) => { if (e.target === overlayRef.current) setShowModal(false) }}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900">Arrêté de caisse</h3>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Vendeur</label>
                <select
                  value={selectedVendorId}
                  onChange={(e) => handleVendorChange(e.target.value === '' ? '' : Number(e.target.value))}
                  className="w-full text-sm border border-gray-200 rounded-xl px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white"
                >
                  <option value="">Sélectionner…</option>
                  {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>
              {selectedVendorId !== '' && (
                <div className="bg-gray-50 rounded-xl px-4 py-3">
                  <p className="text-xs text-gray-500">Montant attendu (depuis dernier arrêté)</p>
                  <p className="text-xl font-bold font-mono text-gray-900 mt-0.5">
                    {loadingBalance ? '…' : expectedAmount !== null ? formatFCFA(expectedAmount) : '—'}
                  </p>
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Montant remis (FCFA)</label>
                <input
                  type="number"
                  min="0"
                  value={actualAmount}
                  onChange={(e) => setActualAmount(e.target.value)}
                  placeholder="0"
                  className="w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                />
                {discrepancy !== null && (
                  <p className={`text-xs mt-1 font-semibold ${discrepancy === 0 ? 'text-gray-400' : discrepancy > 0 ? 'text-success-600' : 'text-red-600'}`}>
                    Écart : {discrepancy > 0 ? '+' : ''}{formatFCFA(discrepancy)}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Notes (optionnel)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 resize-none"
                  placeholder="Remarques…"
                />
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 rounded-xl transition-colors">
                Annuler
              </button>
              <button
                onClick={handleSubmit}
                disabled={!selectedVendorId || actualAmount === '' || submitting}
                className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-xl transition-colors disabled:opacity-60 shadow-sm shadow-primary-200"
              >
                {submitting && <Spinner className="w-4 h-4 text-white" />}
                Valider l'arrêté
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export function EventDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const currentUser = useAuthStore((s) => s.user)
  const [event, setEvent] = useState<Event | null>(null)
  const [stats, setStats] = useState<EventStats | null>(null)
  const [vendors, setVendors] = useState<EventVendor[]>([])
  const [settlements, setSettlements] = useState<CashRegisterSettlement[]>([])
  const [loading, setLoading] = useState(true)
  const [publishing, setPublishing] = useState(false)
  const [closing, setClosing] = useState(false)

  const isAdmin = currentUser?.role === 'admin'
  const isAdminOrSupervisor = isAdmin || currentUser?.role === 'supervisor'

  const isEventPast = !!event && new Date(event.event_date) < new Date(new Date().toDateString())

  useEffect(() => {
    if (!id) return
    const numId = Number(id)
    Promise.all([eventsApi.get(numId), eventsApi.stats(numId)])
      .then(([ev, st]) => { setEvent(ev); setStats(st) })
      .catch(() => toast.error('Impossible de charger l\'événement.'))
      .finally(() => setLoading(false))

    if (isAdmin) {
      eventsApi.listVendors(numId).then(setVendors).catch(() => {})
    }
    if (isAdminOrSupervisor) {
      eventsApi.listSettlements(numId).then(setSettlements).catch(() => {})
    }
  }, [id])

  const handlePublish = async () => {
    if (!event) return
    setPublishing(true)
    try {
      await eventsApi.publish(event.id)
      setEvent((e) => e ? { ...e, status: 'published' } : e)
      toast.success('Événement publié.')
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erreur.'
      toast.error(msg)
    } finally {
      setPublishing(false)
    }
  }

  const handleClose = async () => {
    if (!event) return
    if (!window.confirm('Clôturer cet événement ? Les vendeurs n\'y auront plus accès.')) return
    setClosing(true)
    try {
      await eventsApi.close(event.id)
      setEvent((e) => e ? { ...e, status: 'completed' } : e)
      toast.success('Événement clôturé.')
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erreur.'
      toast.error(msg)
    } finally {
      setClosing(false)
    }
  }

  const copyLink = () => {
    if (!event) return
    navigator.clipboard.writeText(event.public_url)
    toast.success('Lien copié.')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner className="w-6 h-6" />
      </div>
    )
  }

  if (!event) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-3 text-gray-400">
        <svg className="w-12 h-12 opacity-30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
          <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
        <p className="font-medium">Événement introuvable.</p>
      </div>
    )
  }

  const hasPoster = Boolean(event.poster_url)
  const hasMatches = event.matches.length > 0
  const hasStats = stats && stats.by_ticket_type.length > 0
  const hasWhatsApp = stats && (stats.whatsapp_sent > 0 || stats.whatsapp_pending > 0)
  const hasChannelData = stats && (stats.physical_sold > 0 || stats.online_sold > 0)

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ─── HERO ─────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden bg-gray-900">
        {/* Poster blur backdrop */}
        {hasPoster && (
          <img
            src={event.poster_url!}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-20 blur-xl scale-110"
          />
        )}
        {/* Gradient overlay */}
        <div className={`absolute inset-0 ${hasPoster
          ? 'bg-gradient-to-br from-gray-900/90 via-gray-900/70 to-primary-900/80'
          : 'bg-gradient-to-br from-primary-900 via-gray-900 to-gray-900'
        }`} />

        {/* Decorative grid */}
        <div className="absolute inset-0 opacity-5"
          style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.3) 1px, transparent 1px)', backgroundSize: '40px 40px' }}
        />

        <div className="relative px-8 pt-6 pb-8">
          {/* Back link */}
          <button
            onClick={() => navigate('/events')}
            className="flex items-center gap-1.5 text-sm text-white/60 hover:text-white/90 mb-6 transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            Tous les événements
          </button>

          <div className="flex items-end justify-between gap-6 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-3">
                <Badge label={event.status} variant={event.status} />
                {event.published_at && (
                  <span className="text-xs text-white/40">Publié le {formatDate(event.published_at)}</span>
                )}
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white leading-tight tracking-tight mb-3">
                {event.name}
              </h1>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/60">
                <span className="flex items-center gap-1.5">
                  <svg className="w-4 h-4 text-white/40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                  {formatDate(event.event_date)}
                </span>
                <span className="flex items-center gap-1.5">
                  <svg className="w-4 h-4 text-white/40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                  </svg>
                  {event.event_time.slice(0, 5)}
                </span>
                <span className="flex items-center gap-1.5">
                  <svg className="w-4 h-4 text-white/40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
                  </svg>
                  {event.location}
                </span>
              </div>
              {event.description && (
                <p className="mt-3 text-sm text-white/50 max-w-2xl line-clamp-2">{event.description}</p>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 shrink-0">
              {event.status === 'published' && (
                <button
                  onClick={copyLink}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white text-sm font-medium transition-all backdrop-blur-sm"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2"/>
                    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                  </svg>
                  Copier le lien
                </button>
              )}
              {event.status === 'published' && isAdminOrSupervisor && isEventPast && (
                <button
                  onClick={handleClose}
                  disabled={closing}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-danger-500 hover:bg-danger-600 text-white text-sm font-semibold transition-all shadow-lg shadow-danger-500/30 disabled:opacity-60"
                >
                  {closing
                    ? <Spinner className="w-4 h-4 text-white" />
                    : <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 9h6v6H9z"/>
                      </svg>
                  }
                  Clôturer l'événement
                </button>
              )}
              {event.status === 'draft' && (
                <button
                  onClick={handlePublish}
                  disabled={publishing}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-success-500 hover:bg-success-600 text-white text-sm font-semibold transition-all shadow-lg shadow-success-500/30 disabled:opacity-60"
                >
                  {publishing
                    ? <Spinner className="w-4 h-4 text-white" />
                    : <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                  }
                  Publier l'événement
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ─── CONTENT ──────────────────────────────────────────────── */}
      <div className="px-8 py-8 space-y-8">

        {/* KPI Strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            label="Tickets vendus"
            value={event.tickets_sold_total}
            sub={`sur ${event.ticket_types.reduce((a, t) => a + (t.capacity || 0), 0) || '∞'} disponibles`}
            accent="bg-primary-50 text-primary-600"
            icon={
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 5v2M15 11v2M15 17v2M5 5h14a2 2 0 012 2v3a2 2 0 000 4v3a2 2 0 01-2 2H5a2 2 0 01-2-2v-3a2 2 0 000-4V7a2 2 0 012-2z"/>
              </svg>
            }
          />
          <KpiCard
            label="Revenus totaux"
            value={formatFCFA(event.revenue_total)}
            accent="bg-success-50 text-success-600"
            icon={
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
              </svg>
            }
          />
          <KpiCard
            label="Entrées validées"
            value={stats?.entries_count ?? 0}
            sub="scans QR confirmés"
            accent="bg-amber-50 text-amber-600"
            icon={
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            }
          />
          <KpiCard
            label="Catégories"
            value={event.ticket_types.length}
            sub={`${event.ticket_types.filter(t => t.is_active).length} actives`}
            accent="bg-violet-50 text-violet-600"
            icon={
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z"/>
              </svg>
            }
          />
        </div>

        {/* Main grid */}
        <div className={`grid gap-6 ${(hasMatches || hasWhatsApp) ? 'grid-cols-1 xl:grid-cols-3' : 'grid-cols-1'}`}>

          {/* ── Catégories table ── */}
          <div className={`${(hasMatches || hasWhatsApp) ? 'xl:col-span-2' : ''}`}>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <svg className="w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z"/>
                  </svg>
                  Catégories de tickets
                </h2>
                <span className="text-xs text-gray-400 font-mono">{event.ticket_types.length} type{event.ticket_types.length > 1 ? 's' : ''}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50/70">
                      {['Catégorie', 'Prix unitaire', 'Vendus / Capacité', 'Taux de vente', 'Statut'].map((h) => (
                        <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {event.ticket_types.map((t, i) => {
                      const color = CATEGORY_COLORS[i % CATEGORY_COLORS.length]
                      return (
                        <tr key={t.id} className="border-t border-gray-50 hover:bg-gray-50/60 transition-colors group">
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-2.5">
                              <div className={`w-1 h-8 rounded-full ${color.bg} opacity-60 group-hover:opacity-100 transition-opacity`} />
                              <TicketTypeBadge name={t.name} sortOrder={i} size="md" />
                            </div>
                          </td>
                          <td className="px-5 py-3.5 text-sm font-mono font-semibold text-gray-900">
                            {formatFCFA(t.price)}
                          </td>
                          <td className="px-5 py-3.5">
                            <span className="text-sm font-mono text-gray-900 font-semibold">{t.tickets_sold}</span>
                            <span className="text-sm text-gray-400 font-mono">
                              {t.capacity > 0 ? ` / ${t.capacity}` : ' / ∞'}
                            </span>
                          </td>
                          <td className="px-5 py-3.5">
                            <SellThroughBar sold={t.tickets_sold} capacity={t.capacity} colorClass={color.bar} />
                          </td>
                          <td className="px-5 py-3.5">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                              t.is_active
                                ? 'bg-success-50 text-success-700'
                                : 'bg-gray-100 text-gray-400'
                            }`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${t.is_active ? 'bg-success-500' : 'bg-gray-300'}`} />
                              {t.is_active ? 'Actif' : 'Inactif'}
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* ── Right column ── */}
          {(hasMatches || hasWhatsApp) && (
            <div className="flex flex-col gap-6">

              {/* Programme */}
              {hasMatches && (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="px-5 py-4 border-b border-gray-100">
                    <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <svg className="w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/>
                      </svg>
                      Programme
                    </h2>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {event.matches.map((m) => (
                      <div key={m.id} className="px-5 py-3.5">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <span className="text-sm font-semibold text-gray-900 truncate">{m.home_team}</span>
                            <span className="text-xs font-bold text-gray-300 shrink-0">VS</span>
                            <span className="text-sm font-semibold text-gray-900 truncate">{m.away_team}</span>
                          </div>
                          {m.match_time && (
                            <span className="text-xs font-mono text-gray-400 shrink-0 bg-gray-50 px-2 py-1 rounded-lg">
                              {m.match_time.slice(0, 5)}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* WhatsApp stats */}
              {hasWhatsApp && (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="px-5 py-4 border-b border-gray-100">
                    <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <svg className="w-4 h-4 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      Livraison WhatsApp
                    </h2>
                  </div>
                  <div className="px-5 py-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Envoyés</span>
                      <span className="text-sm font-bold font-mono text-green-600">{stats!.whatsapp_sent}</span>
                    </div>
                    {stats!.whatsapp_pending > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">En attente</span>
                        <span className="text-sm font-bold font-mono text-warning-600">{stats!.whatsapp_pending}</span>
                      </div>
                    )}
                    {(stats!.whatsapp_sent + stats!.whatsapp_pending) > 0 && (
                      <div className="pt-2">
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex">
                          <div
                            className="h-full bg-green-500 rounded-full transition-all"
                            style={{ width: `${Math.round((stats!.whatsapp_sent / (stats!.whatsapp_sent + stats!.whatsapp_pending)) * 100)}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-400 mt-1.5 text-right">
                          {Math.round((stats!.whatsapp_sent / (stats!.whatsapp_sent + stats!.whatsapp_pending)) * 100)}% délivré
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Event meta */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4">
                <h2 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <svg className="w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  Informations
                </h2>
                <dl className="space-y-2.5 text-sm">
                  {event.supervisor && (
                    <div className="flex justify-between gap-4">
                      <dt className="text-gray-400">Superviseur</dt>
                      <dd>
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold bg-violet-50 text-violet-700">
                          <span className="w-3.5 h-3.5 rounded-full bg-violet-200 flex items-center justify-center text-[9px] font-bold">
                            {event.supervisor.name[0].toUpperCase()}
                          </span>
                          {event.supervisor.name}
                        </span>
                      </dd>
                    </div>
                  )}
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-400">Créé le</dt>
                    <dd className="text-gray-700 font-medium">{formatDate(event.created_at)}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-400">Slug public</dt>
                    <dd className="text-gray-700 font-mono text-xs bg-gray-50 px-2 py-0.5 rounded">{event.public_slug}</dd>
                  </div>
                  {event.poster_url && (
                    <div className="pt-2">
                      <dt className="text-gray-400 mb-1.5">Affiche</dt>
                      <dd>
                        <img src={event.poster_url} alt="Affiche" className="w-full rounded-xl object-cover aspect-video" />
                      </dd>
                    </div>
                  )}
                </dl>
              </div>

            </div>
          )}
        </div>

        {/* ── Stat cards par type ── */}
        {hasStats && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                </svg>
                Statistiques par catégorie
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {stats!.by_ticket_type.map((s, i) => (
                <StatCard key={s.id} stat={s} index={i} />
              ))}
            </div>
          </div>
        )}

        {/* ── Canaux de vente + Vendeurs + Arrêtés ── */}
        {(hasChannelData || isAdmin || isAdminOrSupervisor) && (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2 space-y-6">
              {hasChannelData && <ChannelBreakdownCard stats={stats!} />}
              {isAdminOrSupervisor && (
                <SettlementSection
                  eventId={Number(id)}
                  vendors={vendors}
                  settlements={settlements}
                  onSettlementCreated={(s) => setSettlements((prev) => [s, ...prev])}
                />
              )}
            </div>
            {isAdmin && (
              <div>
                <VendorPanel
                  eventId={Number(id)}
                  vendors={vendors}
                  onVendorsChange={setVendors}
                />
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  )
}
